import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const Services = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-background">
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#5F72FF22 1px, transparent 1px), linear-gradient(90deg, #5F72FF22 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              ></div>
            </div>

            {/* Animated gradient blobs */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0.3 }}
              animate={{
                scale: [0.8, 1.2, 0.9, 1.1, 0.8],
                opacity: [0.3, 0.4, 0.2, 0.5, 0.3],
                x: ["-10%", "5%", "0%", "-5%", "-10%"],
                y: ["5%", "-5%", "10%", "0%", "5%"],
              }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, repeatType: "loop" }}
              className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] rounded-full bg-accent opacity-20 blur-[100px]"
            />
          </div>
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-futuristic"
            >
              What We Do
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white mb-8"
            >
              We specialize in integrating advanced artificial intelligence and generative AI into your daily
              operations—transforming customer service workflows to boost efficiency, enhance customer retention, and
              improve issue resolution.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2069&q=80"
                  alt="AI Customer Service"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Intelligent Customer Service Solutions</h2>
              <p className="text-white mb-6">
                Our cutting-edge tools empower your customer service and support teams to deliver precise, timely
                responses with minimal manual input, seamlessly integrating into existing platforms and processes.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Automated response generation for common inquiries",
                  "Sentiment analysis to prioritize urgent customer needs",
                  "Contextual understanding of customer history",
                  "Seamless handoff between AI and human agents",
                  "Multi-channel support (email, chat, social media)",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 md:order-2"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                  alt="Order Management"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 md:order-1"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Streamlined Order Management System</h2>
              <p className="text-white mb-6">
                Streamline your order management, delivery logistics, and operations management, all while driving
                increased productivity and customer satisfaction.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Automated order processing and tracking",
                  "Predictive inventory management",
                  "Intelligent routing and delivery optimization",
                  "Proactive issue detection and resolution",
                  "Customer communication automation",
                  "Automated invoicing and payment reconciliation",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative order-2 md:order-1"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                  alt="Advanced Analytics"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Strategic Analytics & Business Intelligence</h2>
              <p className="text-white mb-6">
                Leverage our state-of-the-art large language models (LLMs), machine learning, and intelligent agentic
                workflows to gain valuable insights and make data-driven decisions.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Customer behavior analysis and segmentation",
                  "Service performance metrics and optimization",
                  "Predictive analytics for business forecasting",
                  "Sentiment analysis across customer touchpoints",
                  "Customizable dashboards and reporting",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 md:order-1"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Enterprise System Integration & Automation</h2>
              <p className="text-white mb-6">
                Transform your business operations with our AI-powered integration solutions that seamlessly connect your enterprise systems, enhancing efficiency and providing a unified view of your business data.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "AI-enhanced ERP/CRM system integration and automation",
                  "Intelligent data synchronization across platforms",
                  "Automated workflow creation and optimization",
                  "Ticketing system integration for streamlined issue resolution",
                  "Real-time analytics and reporting across integrated systems",
                  "Custom AI agents tailored to your specific business processes",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 md:order-2"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                  alt="Seamless ERP/CRM Integration"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative order-2 md:order-1"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1523800503107-5bc3ba2a6f81?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2080&q=80"
                  alt="Computer Vision for E-commerce"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Visual AI for E-commerce Excellence</h2>
              <p className="text-white mb-6">
                Revolutionize your e-commerce business with our advanced computer vision solutions that enhance product visualization, streamline catalog management, and create immersive shopping experiences for your customers.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "AI-powered product image enhancement and standardization",
                  "Automated product categorization and tagging",
                  "Virtual try-on and product visualization technologies",
                  "AI-generated product photography and 3D modeling",
                  "Visual search capabilities for your product catalog",
                  "Personalized visual recommendations based on user preferences",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 md:order-1"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Advanced Manufacturing Intelligence</h2>
              <p className="text-white mb-6">
                Optimize your manufacturing processes with our cutting-edge AI solutions that enhance quality control, streamline production, and revolutionize device testing for next-generation products.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "AI-powered quality inspection and defect detection",
                  "Predictive maintenance for manufacturing equipment",
                  "Advanced AR/VR device testing and validation",
                  "Automated production line optimization",
                  "Real-time performance monitoring and analytics",
                  "Computer vision for precision assembly verification",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <svg
                      className="w-5 h-5 text-accent mt-1 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-white">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20 inline-block"
              >
                Learn More
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 md:order-2"
            >
              <div className="rounded-lg overflow-hidden shadow-xl border-2 border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                  alt="AI for Manufacturing"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-accent/30 to-accent2/30 relative overflow-hidden">
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent opacity-20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent2 opacity-20 blur-3xl animate-pulse-slower"></div>
        
        <div className="container-custom relative z-10">
          <div className="backdrop-blur-lg bg-background/80 border border-white/20 rounded-3xl p-12 md:p-16 max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-6 text-white text-center font-futuristic"
            >
              Ready to <span className="bg-gradient-to-r from-accent to-highlight bg-clip-text text-transparent">Transform</span> Your Business?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl mb-8 text-white text-center"
            >
              Contact us today to learn how our AI solutions can be tailored to your specific industry and business
              needs.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex justify-center"
            >
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20"
              >
                Get in Touch
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Services
